import * as React from 'react';

import {ListView} from 'shells/list-view/list-view';

import {shouldShowEmptyStateForType, shouldShowCreatorForType} from './constants';
import type {SequenceTemplateType} from './constants';
import {EmptyListView} from './empty-list-view';
import {sortTemplates} from './sort-templates';
import type {TemplateGroup, TemplateItem} from './template-list';
import {TemplateListHeader} from './template-list-header';
import {TemplateListRow} from './template-list-row';

import styles from './templates.module.css';

type Props = {
    templateType: SequenceTemplateType;
    templates: TemplateGroup;
    onSequenceRename: (templateId: string, currentName: string) => void;
};

export type SortObject = {
    columnName: string;
    order: 'ascending' | 'descending';
};

type State = {
    currentSort: SortObject;
};

const DefaultSort: SortObject = {
    columnName: 'lastSentTime',
    order: 'descending',
};

const DefaultState = {
    currentSort: DefaultSort,
} as const;

export class TemplateListGroup extends React.Component<Props, State> {
    constructor() {
        // @ts-expect-error
        super();
        this.state = DefaultState;
    }

    render() {
        const {templateType, templates} = this.props;
        const {currentSort} = this.state;
        const sortedTemplates = sortTemplates(templates, currentSort);

        return (
            <div className={styles['group-list']}>
                <ListView
                    collection={sortedTemplates}
                    isTable={true}
                    header={
                        <TemplateListHeader
                            showSorting={sortedTemplates.length > 1}
                            currentSort={currentSort}
                            onColumnSort={this.handleColumnSort}
                        />
                    }
                    specialStates={[
                        {
                            shouldRender: this.shouldRenderEmptyState,
                            component: <EmptyListView templateType={templateType} />,
                        },
                    ]}
                    renderRow={this.renderRow}
                />
            </div>
        );
    }

    renderRow = (template: TemplateItem) => (
        <TemplateListRow
            key={template.id}
            template={template}
            showCreator={this.shouldShowCreator()}
            onSequenceRename={this.props.onSequenceRename}
        />
    );

    shouldRenderEmptyState = (): boolean => {
        return shouldShowEmptyStateForType(this.props.templateType) && !this.props.templates.length;
    };

    shouldShowCreator(): boolean {
        return shouldShowCreatorForType(this.props.templateType);
    }

    handleColumnSort = (columnName: string) => {
        const {currentSort} = this.state;

        const newSort: SortObject =
            columnName === currentSort.columnName
                ? toggleSortOrder(currentSort)
                : {...currentSort, columnName};

        this.setState({
            currentSort: newSort,
        });
    };
}

function toggleSortOrder(currentSort: SortObject): SortObject {
    const newOrder = currentSort.order === 'ascending' ? 'descending' : 'ascending';

    return {...currentSort, order: newOrder};
}
